import { getJWT, accessControl } from '@lighthouse-web3/kavach';
import lighthouse from "@lighthouse-web3/sdk";
import axios from "axios";
import { notify } from "./notification";
import {
  getAccessToken,
  getAddress,
  getBlockchainType,
  getLoginType,
  logout,
} from "./auth";
import { baseUrl, encryptedBaseUrl } from "../config/urls";
import { solana_sign_auth_message } from "./solana";
import store from "../../store/store";
import {  balanceAC, fileAC } from "../../store/action-creators";
import moment from "moment";
import { sign_auth_message_web3auth } from "./web3auth";
import { sign_auth_message_metamask } from "./evm";
import { changeStore } from "../../store/useStore";
import { coreum_sign_auth_message } from "./coreum";
import { clipText } from './other';
import Web3 from "web3";
import { IUploadProgressCallback } from '@lighthouse-web3/sdk/dist/types';
import { radix_sign_auth_message } from './radix';



let currentProgressFunction:any = null;

export const sign_auth_message = async (): Promise<string | void> => {
  const blockchain = getBlockchainType();
  const loginType = getLoginType();
  let signedMessage: string | void;
  switch (blockchain) {
    case "EVM":
      signedMessage =
        loginType === "metamask"
          ? await sign_auth_message_metamask()
          : await sign_auth_message_web3auth();
      break;
    case "Solana":
      signedMessage = await solana_sign_auth_message();
      break;
    case "Coreum":
      signedMessage = await coreum_sign_auth_message();
      break;
    case "Radix":
      signedMessage = await radix_sign_auth_message();
      break;
    default:
      signedMessage = await sign_auth_message_metamask();
      break;
  }
  return signedMessage;
};

export const setJWTKey = async (publicKey: string, signedAuthMessage: string): Promise<string | undefined> => {
  try {
  
    const response = await getJWT(publicKey, signedAuthMessage);
    const currentTime = moment();
    const expiryTime = currentTime.add(5, "hours");
    const expiryTimestamp = expiryTime.valueOf();
    let authState = store?.getState()?.["auth"];
    if (response?.JWT) {
      await changeStore('login', {
        ...authState,
        JWT: response.JWT,
        JWTexpiry: expiryTimestamp,
      })
    }
    return response?.JWT;
  } catch (error) {
    return signedAuthMessage;
  }
};

export const getKavachJWT = async (): Promise<string | null | undefined> => {
  try {
    let AuthObj = store.getState()?.["auth"];
    let JWT: string | undefined = undefined;
    if (moment(AuthObj?.JWTexpiry).isAfter(moment().valueOf()) && AuthObj?.JWT) {
      JWT = AuthObj.JWT;
    } else {
      const signed_message = await sign_auth_message();
      if(signed_message){
        JWT = await setJWTKey(getAddress() || '', signed_message || '');
      }else{
        throw new Error('MESSAGE SIGNATURE NOT FOUND')
      }
    }
    return JWT;
  } catch (error) {
    console.error(error);
  }
};

const getFileList = async (pageNumber: number = 1, pageSize: number = 1000): Promise<{
  filesArr: any[];
  totalPages: number;
  pageNumber: number;
  totalFiles: number;
  pageSize: number;
}> => {
  let filesArr: any[] = [];
  let totalFiles: number = 0;
  let totalPages: number = 0;
  try {
    let URL: string = "";

    if (pageNumber === 1) {
      URL = `${baseUrl}/api/user/files_uploaded`;
    } else {
      let lastIDIndex: number = (pageNumber - 1) * pageSize - 1;
      const currentFilesList: any[] = store.getState()?.file?.filesArr || [];
      let lastKey: string = currentFilesList[lastIDIndex]?.id;

      URL = `${baseUrl}/api/user/files_uploaded?lastKey=${lastKey}`;
    }
    const response = await axios.get(URL, {
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
    });  
    if (response["status"] === 200) {
      filesArr = response["data"]?.["fileList"];
      totalFiles = response["data"]?.["totalFiles"];
      totalPages = Math.ceil(totalFiles / pageSize);
      const lastElement = filesArr?.slice(-1)[0];    
      if (lastElement) {
        const createdAt = lastElement.createdAt;  
        const thirtyDaysAgo = moment().subtract(30, 'days');
        const isOlderThanThirtyDays = moment(createdAt).isBefore(thirtyDaysAgo);
        changeStore("setData", { isOlderThanThirtyDays });
      }else{
        changeStore("setData", { isOlderThanThirtyDays:false });
      }

    }
  } catch (e) {
    notify(`Error: ${e}`, "error");
  }

  return {
    filesArr,
    totalPages,
    pageNumber,
    totalFiles,
    pageSize,
  };
};

export const getUploadedFiles = async (pageNumber: number = 1): Promise<void> => {
  const fileData = await getFileList(pageNumber);
  await store.dispatch(fileAC.setFileData(fileData));
  await getBalance();
};

const progressCallback = (progressData: IUploadProgressCallback) => {
  const currentProgress:number = parseFloat((progressData?.progress * 100).toFixed(1))
  currentProgress < 1 ? currentProgressFunction(1) : currentProgressFunction(currentProgress)
};

export const uploadFile = async (
  uploadedFile: React.ChangeEvent<HTMLInputElement>,
  setUploadProgress: any,
): Promise<void> => {
  uploadedFile.persist();
    try {
      let balance = store.getState()["balance"];
      const remainingBalance = (+balance?.dataLimit - +balance?.dataUsed) || 0;
      const fileSize = uploadedFile?.target?.files?.[0]?.size || remainingBalance+1
  
      if (remainingBalance >= fileSize) {
        currentProgressFunction = setUploadProgress;
        const accessToken = getAccessToken() || '';
        const deploy_response = await lighthouse.upload(
          uploadedFile.target.files,
          accessToken,
          undefined,
          progressCallback
        );
        setTimeout(getUploadedFiles, 1500);
        notify(
          `File Upload Success:  ${clipText(deploy_response?.data?.Hash, 10)}`,
          "success"
        );
        setUploadProgress(0);
      } else {
        setUploadProgress(0);
        notify(`Data Limit Exceeded, Please Upgrade the Plan`, "error");
      }
    } catch (e) {
      notify(`ERROR:${e}`, "error");
      setUploadProgress(0);
    }
};

export const uploadEncryptedFile = async (
  uploadedFile: React.ChangeEvent<HTMLInputElement>,
  setUploadProgress: any,
): Promise<void> => {
  // uploadedFile.persist();
  try {
      let balance = store.getState()["balance"];

      const remainingBalance = (+balance?.dataLimit - +balance?.dataUsed) || 0;
      const fileSize = uploadedFile?.target?.files?.[0]?.size || remainingBalance+1
      if (remainingBalance >= fileSize) {
        const JWT = await getKavachJWT();
        if (!JWT) throw new Error('JWT Key not found')
      
        currentProgressFunction = setUploadProgress;
        const fileArr: any = uploadedFile?.target?.files?.[0]
        const deploy_response = await lighthouse.uploadEncrypted(
          [fileArr],
          getAccessToken() || '',
          getAddress() || '',
          JWT || '',
          progressCallback
        );      

    setTimeout(getUploadedFiles, 1500);
    notify(
      `File Upload Success:  ${clipText(deploy_response?.data[0]?.Hash, 10)}`,
      "success"
    );
      } else {
        setUploadProgress(0);
        notify(`Data Limit Exceeded, Please Upgrade the Plan`, "error");
      }
    } catch (e) {
      notify(`${e}`, "error");
      console.error(e);
      setUploadProgress(0);
    }

    setUploadProgress(0);
};

export const getDecryptKey = async (cid: string): Promise<string> => {
  const JWT = await getKavachJWT() || '';
  const publicKey = getAddress() || '';
  const key = await lighthouse.fetchEncryptionKey(cid, publicKey, JWT);
  return key?.data?.key || '';
};

export const decryptEncryptedFile = async (cid: string): Promise<any> => {
  const key = await getDecryptKey(cid);
  const decrypted = await lighthouse.decryptFile(cid, key);
  return decrypted;
};

export const createMigration = async (cids: string[]): Promise<any> => {
try {
  const response = await axios.post(
    `${baseUrl}/api/lighthouse/migration_request`,
    {
      data: JSON.stringify(cids),
    },
    {
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
      maxContentLength: 100000000,
      maxBodyLength: 1000000000,
    }
  );
  // await awardFirstMigration()
  return response.status === 200 ? response : false;  
} catch (error) {
  throw new Error(error+'');
}
};



export const uploadFolder = async (
  uploadedFile: React.ChangeEvent<HTMLInputElement>,
  setUploadProgress: any
): Promise<void> => {
  uploadedFile.persist();
    try {
      const balance = store.getState()?.balance;
      const remainingBalance = (+balance?.dataLimit - +balance?.dataUsed) || 0;
      let totalFolderSize = 0;
      const files = uploadedFile?.target?.files;
      if (files) {
        for (let i = 0; i < files.length; i++) {
          totalFolderSize += files[i].size;
        }
      }
      if (remainingBalance >= totalFolderSize ) {
        currentProgressFunction = setUploadProgress;
        const accessToken = getAccessToken() || '';
        const deploy_response = await lighthouse.upload(
          uploadedFile.target.files,
          accessToken,
          undefined,
          progressCallback
        );
        // awardFirstuploadPoint('public');
        setTimeout(getUploadedFiles, 1500);
        //
        notify(
          `Folder Upload Success:\n  ${clipText(deploy_response?.data?.Hash, 10)}`,
          "success"
        );
        setUploadProgress(0);
      } else {
        setUploadProgress(0);
        notify(`Data Limit Exceeded, Please Upgrade the Plan`, "error");
      }
    } catch (e) {
      notify(`ERROR: ${e}`, "error");
      setUploadProgress(0);
    }
};

export const getBalance = async (): Promise<void> => {
  let balanceObj:any = {
    dataLimit: 0,
    dataUsed: 0,
  };
  try {
    const response = await axios.get(`${baseUrl}/api/auth/get_profile`, {
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
    });
    balanceObj = response.data;
  } catch (error) {
    notify(`ERROR: ${error}`, "error");
  }
  if (balanceObj?.email && balanceObj?.email.includes("null-")) {
    changeStore("setData", { isVerifyEmailDialog: true });
  }
  // console.log('----', balanceObj)
  await store.dispatch(balanceAC.setBalanceData(balanceObj));
};

export const getDealIDs = async (cid: string): Promise<any[] | null> => {
  const status = await lighthouse.dealStatus(cid);
  var dealIDs = [];  
   if (Array.isArray(status.data) && status.data.every((item: any) => typeof item.DealID === 'number')) {
    const formattedDealIds = status.data.map((deal: any) => deal.DealID); 
    dealIDs = formattedDealIds; 
   } else {   
    const formattedDealIds = status.data.map((deal: any) => deal.deal.map((dealDetail: any) => dealDetail.dealId));
    const flatDealIds = formattedDealIds.flat();
    dealIDs = flatDealIds;
   }
  return dealIDs.length > 0 ? dealIDs : null;
};

export const createAccessControl = async (
  cid: string,
  conditions: any[],
  aggregator: string
): Promise<any> => {
  // Extract all IDs from aggregator string using regex
  const idRegex = /\[(\d+)\]/g;
  const matches = aggregator.match(idRegex);
  const idsInAggregator = matches ? matches.map(match => parseInt(match.replace(/[\[\]]/g, ''))) : [];

  // Filter conditions to only keep those with IDs present in aggregator
  conditions = conditions.filter(condition => idsInAggregator.includes(condition.id));
  
  const publicKey = getAddress() || '';
  const JWT = await getKavachJWT() || '';
  const chainType: any = getBlockchainType()?.toLowerCase() as any || undefined;

  const { isSuccess, error } = await accessControl(
    publicKey,
    cid,
    JWT,
    conditions,
    aggregator,
    chainType
  );

  if (error) {
    throw error
  }
  // console.log(response,'response ---')
  return { data: { cid: cid, status: 'Success' } };
  
};

export const shareFileToAddress = async (
  cid: string,
  publicAddress: string[]
): Promise<any> => {
  const JWT = await getKavachJWT() || '';
  const address = await getAddress() || '';
  const res = await lighthouse.shareFile(
    address,
    publicAddress,
    cid,
    JWT
  );
  return res;
};

export const getCIDAccessConditions = async (cid: string): Promise<any> => {
  const response = await axios.get(
    `${encryptedBaseUrl}/api/fileAccessConditions/get/${cid}`
  );
  return response.status === 200 && response.data;
};


export const revokeShareAccess = async (userPublicKey:string, cid:string):Promise<any> =>{
  const JWT = await getKavachJWT() || '';
  const publicKey = await getAddress() || '';
  const response = await lighthouse.revokeFileAccess(
    publicKey,
    userPublicKey,
    cid,
    JWT
  );
  return response;
}

export const getUserAPIKeys = async (): Promise<any> => {
  try {
    const response = await axios.get(`${baseUrl}/api/auth/get_user_keys`, {
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
    });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    notify(`Error: ${error}`, "error");
    logout();
  }
};

export const createAPIKey = async (keyName: string): Promise<any> => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/auth/create_api_key?keyName=${keyName}`,
      {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }
    );
    if (response) {
      notify("API Key Created", "success");
      return response.data;
    }
  } catch (error) {
    notify(`Error: ${error}`, "error");
  }
};

export const deleteAPIKey = async (keyID: string): Promise<boolean | void> => {
  try {
    const response = await axios.delete(
      `${baseUrl}/api/auth/remove_api_key?keyId=${keyID}`,
      {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }
    );
    if (response) {
      notify(`Key Deleted`, "success");
      return true;
    }
  } catch (error) {
    notify(`Error: ${error}`, "error");
    logout();
  }
};
export const deleteFileKey = async (keyID: string): Promise<boolean | void> => {
  try {
    const response = await axios.delete(
      `${baseUrl}/api/user/delete_file?id=${keyID}`,
      {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }
    );
    if (response) {
      notify(`File Deleted`, "success");
      getUploadedFiles()
      return true;
    }
  } catch (error) {
    notify(`Error: ${error}`, "error");
    logout();
  }
};

export const getActivePlanList = async (): Promise<any> => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/topup/get_active_plan_list`,
      {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }
    );
    if (response?.status === 200) {
      //
      return response.data;
    }
  } catch (error) {
    notify(`Error: ${error}`, "error");
    logout();
  }
};